import { apiDelete, apiGet, apiPatch, apiPost } from './client.js';
import type { PaginatedMeta } from './tickets.js';

export type SupplierRma = {
  id: string;
  rmaNumber: string;
  supplierId: string;
  supplierName?: string;
  status: string;
  resolutionType: string;
  creditAmount?: string | null;
  trackingNumber?: string | null;
  carrier?: string | null;
  reason?: string | null;
  createdAt: string;
  items?: SupplierRmaItem[];
};

export type SupplierRmaItem = {
  id: string;
  inventoryItemId: string;
  itemName: string;
  supplierSku?: string | null;
  quantityReturned: number;
  quantityReplaced?: number | null;
  unitCost: string;
  totalCost: string;
  condition?: string | null;
  itemStatus?: string | null;
  creditAmount?: string | null;
  denialReason?: string | null;
  resolutionNote?: string | null;
  inventorySerialId?: string | null;
  partFailureId?: string | null;
};

export function listRmas(params?: {
  status?: string;
  supplierId?: string;
  page?: number;
  pageSize?: number;
}) {
  return apiGet<SupplierRma[], PaginatedMeta>('/api/v1/inventory/rmas', { params });
}

export function getRma(id: string) {
  return apiGet<SupplierRma>(`/api/v1/inventory/rmas/${id}`).then((r) => r.data);
}

export function createRma(body: {
  supplierId: string;
  resolutionType?: string;
  reason?: string | undefined;
  internalNotes?: string | undefined;
  items?: Array<{
    inventoryItemId: string;
    quantityReturned: number;
    unitCost?: number;
    supplierSku?: string;
    condition?: string;
  }>;
  partFailureIds?: string[];
}) {
  return apiPost<SupplierRma>('/api/v1/inventory/rmas', body).then((r) => r.data);
}

export function patchRma(id: string, body: Record<string, unknown>) {
  return apiPatch<SupplierRma>(`/api/v1/inventory/rmas/${id}`, body).then((r) => r.data);
}

export function advanceRmaStatus(id: string, status: string) {
  return apiPost<SupplierRma>(`/api/v1/inventory/rmas/${id}/advance-status`, { status }).then(
    (r) => r.data,
  );
}

export function receiveRmaReplacement(
  id: string,
  items: Array<{ rmaItemId: string; quantityReplaced: number }>,
) {
  return apiPost<SupplierRma>(`/api/v1/inventory/rmas/${id}/receive-replacement`, { items }).then(
    (r) => r.data,
  );
}

export function cancelRma(id: string) {
  return apiDelete<SupplierRma>(`/api/v1/inventory/rmas/${id}`).then((r) => r.data);
}

export function resolveRma(
  id: string,
  body: {
    mode: 'header' | 'per_item';
    creditAmount?: number;
    creditMemoNumber?: string | undefined;
    items?: Array<{
      id: string;
      itemStatus: 'credited' | 'denied' | 'replaced';
      creditAmount?: number | undefined;
      denialReason?: string | undefined;
      resolutionNote?: string | undefined;
    }>;
  },
) {
  return apiPost<SupplierRma>(`/api/v1/inventory/rmas/${id}/resolve`, body).then((r) => r.data);
}
